import React, { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../config";
import Papa from "papaparse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faTimes,
  faSpinner,
  faDownload,
  faHistory,
  faExclamationTriangle,
  faInfoCircle,
  faTableList,
  faFileCsv,
  faUserPlus,
  faTrash,
  faPlay,
  faCheckCircle,
  faFile,
  faEnvelopeOpenText,
  faMobileAlt,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import BatchFileSpecsModal from "./BatchFileSpecsModal";
const MySwal = withReactContent(Swal);

const MEMBER_ENROLLMENT_TEMPLATE_HEADERS = Object.freeze([
  "Employee_ID",
  "Last_Name",
  "Given_Name",
  "Middle_Name",
  "Suffix",
  "NickName",
  "Birth Year(1990)",
  "Birth Month(1-12)",
  "Birth Day(1-31)",
  "Gender",
  "Civil_Status",
  "Nationality",
  "Philhealth_required",
  "Philhealth_ID",
  "Mob_Tel",
  "Corp_Code",
  "Department",
  "Designation",
  "Employment_Date",
  "Plan_Code",
  "Member_Type",
  "Principal_ID",
  "Activation Date",
  "Email_Address",
  "Expiration Date",
  "Special Instructions",
]);

const UploadMemberEnrollment = () => {
  const [files, setFiles] = useState([]);
  const [successfulUploads, setSuccessfulUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [globalError, setGlobalError] = useState(null); // For enhanced errors
  const [showInfo, setShowInfo] = useState(true);
  const [showSpecsModal, setShowSpecsModal] = useState(false);
  const [uploadLimits, setUploadLimits] = useState(null);
  const [limitsError, setLimitsError] = useState("");
  const [isValidatingFiles, setIsValidatingFiles] = useState(false);
  const validatingFilesRef = useRef(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [showEmailSample, setShowEmailSample] = useState(false);
  const [showSmsSample, setShowSmsSample] = useState(false);



  const allowedFileTypes = ["csv", "xlsx", "xls"];
  const maxUploadLimit = 5;
  const fetchUploadLimits = useCallback(async () => {
    const response = await axios.get(`${BASE_URL}/members/batch-enrollment/limits`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
      },
    });
    const limits = response.data?.data;
    if (
      !Number.isSafeInteger(limits?.max_rows) || limits.max_rows < 1 ||
      !Number.isSafeInteger(limits?.max_file_size_mb) || limits.max_file_size_mb < 1
    ) {
      throw new Error("Invalid enrollment upload limits.");
    }
    setUploadLimits(limits);
    setLimitsError("");
    return limits;
  }, []);

  useEffect(() => {
    fetchUploadLimits().catch(() => {
      setLimitsError("Unable to load upload limits. Select your file to retry.");
    });
  }, [fetchUploadLimits]);
  const headersInfo = [
    {
      header: "Employee_ID",
      required: true,
      description: "Unique employee identifier (required)",
    },
    {
      header: "Last_Name",
      required: true,
      description: "Last name (required)",
    },
    {
      header: "Given_Name",
      required: true,
      description: "First name (required)",
    },
    {
      header: "Middle_Name",
      required: true,
      description: "Middle name or initial (required)",
    },
    {
      header: "Suffix",
      required: false,
      description: "Name suffix (e.g., Jr., Sr.) (optional)",
    },
    { header: "NickName", required: false, description: "Nickname (optional)" },
    {
      header: "Birth Year(1990)",
      required: true,
      description: "Year of birth (e.g., 1990) (required)",
    },
    {
      header: "Birth Month(1-12)",
      required: true,
      description: "Month of birth (1-12) (required)",
    },
    {
      header: "Birth Day(1-31)",
      required: true,
      description: "Day of birth (1-31) (required)",
    },
    {
      header: "Gender",
      required: true,
      description: "Gender (m/f) (required)",
    },
    {
      header: "Civil_Status",
      required: true,
      description: "Civil status (s/m/w/d) (required)",
    },
    {
      header: "Nationality",
      required: false,
      description: "Nationality (optional)",
    },
    {
      header: "Philhealth_required",
      required: false,
      description:
        "PhilHealth required (yes/no or 1/0) (optional; if yes/1, PhilHealth ID required)",
    },
    {
      header: "Philhealth_ID",
      required: false,
      description:
        "PhilHealth ID (required if PhilHealth_required is yes/1; auto-tags PhilHealth_required as yes/1 if provided)",
    },
    {
      header: "Mob_Tel",
      required: false,
      description: "Mobile number (optional)",
    },
    {
      header: "Corp_Code",
      required: true,
      description: "Corporate code (required; must exist and be finalized)",
    },
    {
      header: "Department",
      required: false,
      description: "Department (optional)",
    },
    {
      header: "Designation",
      required: false,
      description: "Designation/Position (optional)",
    },
    {
      header: "Employment_Date",
      required: false,
      description: "Employment date (mm/dd/yyyy) (optional)",
    },
    {
      header: "Plan_Code",
      required: true,
      description: "Plan code (required; must match corp_code and member_type)",
    },
    {
      header: "Member_Type",
      required: true,
      description: "Member type (p for principal, d for dependent) (required)",
    },
    {
      header: "Principal_ID",
      required: false,
      description:
        "Principal ID (required for dependents; must be existing principal; must be blank for principal member_type)",
    },
    {
      header: "Activation Date",
      required: true,
      description: "Activation date (MM/DD/YYYY, strict) (required)",
    },
    {
      header: "Email_Address",
      required: false,
      description: "Email address (optional; must be valid format)",
    },
    {
      header: "Expiration Date",
      required: false,
      description:
        "Expiration date (mm/dd/yyyy) (optional; defaults to corp expiry if blank)",
    },
    {
      header: "Special Instructions",
      required: false,
      description: "Special instructions (optional)",
    },
  ];

  const triggerTemplateDownload = (blob, fileName) => {
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleDownloadCsvTemplate = () => {
    const csvTemplate = Papa.unparse({
      fields: MEMBER_ENROLLMENT_TEMPLATE_HEADERS,
      data: [],
    });
    const blob = new Blob([`${csvTemplate}\n`], {
      type: "text/csv;charset=utf-8;",
    });
    triggerTemplateDownload(blob, "member_batch_enrollment_template.csv");
    toast.success("CSV template downloaded.", { duration: 3000 });
  };

  const handleDownloadExcelTemplate = async () => {
    try {
      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.aoa_to_sheet([
        MEMBER_ENROLLMENT_TEMPLATE_HEADERS,
      ]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
      XLSX.writeFile(workbook, "member_batch_enrollment_template.xlsx");
      toast.success("Excel template downloaded.", { duration: 3000 });
    } catch (error) {
      toast.error("Unable to download Excel template.", { duration: 3000 });
    }
  };

  const validateFile = (file, limits) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const isAllowedType = allowedFileTypes.includes(fileExtension);
    const isWithinSizeLimit = file.size <= limits.max_file_size_mb * 1024 * 1024;
    if (!isAllowedType) {
      return {
        valid: false,
        error: `Unsupported type: ${file.name}. Only ${allowedFileTypes.join(
          ", "
        )} allowed.`,
      };
    }
    if (!isWithinSizeLimit) {
      return {
        valid: false,
        error: `${file.name} exceeds ${limits.max_file_size_mb} MB.`,
      };
    }
    return { valid: true };
  };

  const isStrictMmDdYyyy = (dateValue) => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return false;
    }

    const [monthString, dayString, yearString] = dateValue.split("/");
    const month = Number(monthString);
    const day = Number(dayString);
    const year = Number(yearString);

    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() + 1 === month &&
      date.getUTCDate() === day
    );
  };

  const normalizePhilhealthRequiredFlag = (rawValue) => {
    const normalizedValue = String(rawValue ?? "")
      .trim()
      .toLowerCase();
    if (normalizedValue === "") {
      return null;
    }

    if (["1", "yes", "y", "true"].includes(normalizedValue)) {
      return 1;
    }

    if (["0", "no", "n", "false"].includes(normalizedValue)) {
      return 0;
    }

    return -1;
  };

  const normalizeCodeToSixDigits = (rawValue) => {
    const normalizedValue = String(rawValue ?? "").trim();
    if (normalizedValue === "") {
      return "";
    }

    return normalizedValue.padStart(6, "0");
  };

  const validateCsvEnrollmentRows = (file, maxRows) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      return Promise.resolve({ valid: true });
    }

    return new Promise((resolve) => {
      const corporatePlanCache = new Map();
      let dataRowCount = 0;
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        chunkSize: 512 * 1024,
        chunk: async (results, parser) => {
          parser.pause();
          const rejectFile = (result) => {
            resolve(result);
            parser.abort();
          };
          if (Array.isArray(results?.errors) && results.errors.length > 0) {
            rejectFile({
              valid: false,
              error: `${file.name}: Unable to read CSV structure for Activation Date validation.`,
            });
            return;
          }

          const rows = Array.isArray(results?.data) ? results.data : [];
          for (let index = 0; index < rows.length; index++) {
            dataRowCount += 1;
            if (dataRowCount > maxRows) {
              rejectFile({
                valid: false,
                error: `${file.name}: Maximum ${maxRows.toLocaleString()} data rows per file (excluding the header).`,
              });
              return;
            }
            const row = rows[index] || {};
            const activationDateRaw = String(
              row["Activation Date"] ??
                row["Activation_Date"] ??
                row["ActivationDate"] ??
                ""
            ).trim();
            const philhealthRequiredRaw = String(
              row["Philhealth_required"] ??
                row["PhilHealth_required"] ??
                row["philhealth_required"] ??
                ""
            ).trim();
            const philhealthIdRaw = String(
              row["Philhealth_ID"] ??
                row["PhilHealth_ID"] ??
                row["philhealth_id"] ??
                ""
            ).trim();
            const memberTypeRaw = String(
              row["Member_Type"] ??
                row["Member Type"] ??
                row["member_type"] ??
                ""
            )
              .trim()
              .toLowerCase();
            const principalIdRaw = String(
              row["Principal_ID"] ??
                row["Principal ID"] ??
                row["principal_id"] ??
                ""
            ).trim();
            const corpCodeRaw = String(
              row["Corp_Code"] ??
                row["Corp Code"] ??
                row["corp_code"] ??
                ""
            ).trim();
            const planCodeRaw = String(
              row["Plan_Code"] ??
                row["Plan Code"] ??
                row["plan_code"] ??
                ""
            ).trim();
            const rowNumber = dataRowCount + 1; // Include the header row.

            if (activationDateRaw === "") {
              rejectFile({
                valid: false,
                error: `${file.name}: Row ${rowNumber} has blank Activation Date. Expected MM/DD/YYYY.`,
              });
              return;
            }

            if (!isStrictMmDdYyyy(activationDateRaw)) {
              rejectFile({
                valid: false,
                error: `${file.name}: Row ${rowNumber} has invalid Activation Date '${activationDateRaw}'. Expected strict MM/DD/YYYY.`,
              });
              return;
            }

            if (memberTypeRaw === "p" && principalIdRaw !== "") {
              rejectFile({
                valid: false,
                error: `${file.name}: Row ${rowNumber} has member_type 'p' but Principal_ID is filled. Principal rows must not have Principal_ID.`,
              });
              return;
            }

            if (memberTypeRaw === "d" && principalIdRaw === "") {
              rejectFile({
                valid: false,
                error: `${file.name}: Row ${rowNumber} has member_type 'd' but Principal_ID is blank.`,
              });
              return;
            }

            const normalizedPhilhealthRequired =
              normalizePhilhealthRequiredFlag(philhealthRequiredRaw);
            if (normalizedPhilhealthRequired === -1) {
              rejectFile({
                valid: false,
                error: `${file.name}: Row ${rowNumber} has invalid Philhealth_required '${philhealthRequiredRaw}'. Use yes/no or 1/0.`,
              });
              return;
            }

            const effectivePhilhealthRequired =
              philhealthIdRaw !== "" ? 1 : normalizedPhilhealthRequired;
            if (effectivePhilhealthRequired === 1 && philhealthIdRaw === "") {
              rejectFile({
                valid: false,
                error: `${file.name}: Row ${rowNumber} requires Philhealth_ID when Philhealth_required is yes/1.`,
              });
              return;
            }

            const normalizedCorpCode = normalizeCodeToSixDigits(corpCodeRaw);
            const normalizedPlanCode = normalizeCodeToSixDigits(planCodeRaw);
            if (normalizedCorpCode !== "" && normalizedPlanCode !== "") {
              let corpPlans = corporatePlanCache.get(normalizedCorpCode);
              if (!corpPlans) {
                try {
                  const response = await axios.get(
                    `${BASE_URL}/members/corporates/${normalizedCorpCode}/plans`,
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                          "authentication/token"
                        )}`,
                      },
                    }
                  );
                  const planRows = Array.isArray(response.data?.data)
                    ? response.data.data
                    : [];
                  corpPlans = new Set(
                    planRows.map((plan) =>
                      normalizeCodeToSixDigits(plan?.plan_code)
                    )
                  );
                  corporatePlanCache.set(normalizedCorpCode, corpPlans);
                } catch (error) {
                  rejectFile({
                    valid: false,
                    error: `${file.name}: Unable to validate Plan_Code mapping for Corp_Code '${normalizedCorpCode}' at row ${rowNumber}.`,
                  });
                  return;
                }
              }

              if (!corpPlans.has(normalizedPlanCode)) {
                rejectFile({
                  valid: false,
                  error: `${file.name}: Row ${rowNumber} has Plan_Code '${normalizedPlanCode}' that does not belong to Corp_Code '${normalizedCorpCode}'.`,
                });
                return;
              }
            }
          }

          parser.resume();
        },
        complete: () => resolve({ valid: true }),
        error: () => {
          resolve({
            valid: false,
            error: `${file.name}: Unable to read file for Activation Date validation.`,
          });
        },
      });
    });
  };

  const handleFileChange = async (selectedFiles) => {
    if (validatingFilesRef.current || !selectedFiles?.length) return;
    validatingFilesRef.current = true;
    setIsValidatingFiles(true);
    try {
      // Refresh for every selection so changes in System Settings apply to an open page.
      const limits = await fetchUploadLimits();
      let invalidFiles = [];
      const validFiles = [];

      for (const file of Array.from(selectedFiles)) {
        const baseValidation = validateFile(file, limits);
        if (!baseValidation.valid) {
          invalidFiles.push(baseValidation.error);
          continue;
        }

        const csvActivationDateValidation = await validateCsvEnrollmentRows(file, limits.max_rows);
        if (!csvActivationDateValidation.valid) {
          invalidFiles.push(csvActivationDateValidation.error);
          continue;
        }

        validFiles.push(file);
      }

      if (invalidFiles.length > 0) {
        MySwal.fire({
          icon: "warning",
          title: "Invalid Files",
          html: `<ul>${invalidFiles
            .map((err) => `<li>${err}</li>`)
            .join("")}</ul>`,
          confirmButtonText: "OK",
        });
      }
      if (files.length + validFiles.length > maxUploadLimit) {
        MySwal.fire({
          icon: "error",
          title: "Limit Exceeded",
          text: `Max ${maxUploadLimit} files at once.`,
          confirmButtonText: "Clear and Retry",
          showCancelButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            setFiles([]);
            setSuccessfulUploads([]);
          }
        });
        return;
      }
      const newFiles = validFiles.map((file) => ({
        file,
        progress: 0,
        isUploading: false,
        uploadId: null,
        error: null,
      }));
      setFiles((prev) => [...prev, ...newFiles]);
      newFiles.forEach((fileObj) => handleFileUpload(fileObj));
    } catch (error) {
      setLimitsError("Unable to load upload limits. Select your file to retry.");
      toast.error("Unable to validate files. Please try again.");
    } finally {
      validatingFilesRef.current = false;
      setIsValidatingFiles(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const handleFileUpload = async (fileObj) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );
    try {
      const formData = new FormData();
      formData.append("file", fileObj.file);
      const response = await axios.post(
        `${BASE_URL}/members/batch-enrollment/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setFiles((prev) =>
              prev.map((f) =>
                f.file === fileObj.file ? { ...f, progress: percent } : f
              )
            );
          },
        }
      );
      if (response.data?.upload_id) {
        setFiles((prev) =>
          prev.map((f) =>
            f.file === fileObj.file
              ? {
                  ...f,
                  isUploading: false,
                  progress: 100,
                  uploadId: response.data.upload_id,
                }
              : f
          )
        );
        setSuccessfulUploads((prev) => [...prev, response.data.upload_id]);
        toast.success(`Uploaded: ${fileObj.file.name}`, { duration: 3000 });
      }
    } catch (error) {
      const message = error.response?.data?.message;
      const errorMessage = typeof message === "string"
        ? message
        : message && typeof message === "object"
          ? Object.values(message).flat().join(" ")
          : error.response?.status === 413
            ? "The server rejected this file size. Please ask your administrator to check the server upload limits."
            : "Upload failed.";
      setFiles((prev) =>
        prev.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, error: errorMessage }
            : f
        )
      );
      // Enhanced: Swal for critical errors
      MySwal.fire({
        icon: "error",
        title: "Upload Error",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    }
  };
  const handleStartProcessing = async () => {
    if (successfulUploads.length === 0) {
      toast.error("No files to process.", { duration: 4000 });
      return;
    }
    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/members/batch-enrollment/process`,
        { upload_ids: successfulUploads },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      // toast.success(response.data.message, {
      //   duration: 4000,
      //   action: {
      //     label: "View Uploaded File History",
      //     onClick: () => navigate("/history-member-batch-enrollment"),
      //   },
      // });
      // setFiles([]);
      // setSuccessfulUploads([]);
      // Show success message first
      toast.success(response.data.message, { duration: 3000 });

      // Then show SweetAlert confirmation modal
      await MySwal.fire({
        title: "Batch Processing Started",
        text: "Your batch is now being processed. Would you like to view the upload history to monitor progress?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, view batch progress",
        cancelButtonText: "Stay here",
        reverseButtons: true,
        confirmButtonColor: "#2563eb", // Tailwind blue-600
        cancelButtonColor: "#9ca3af", // Tailwind gray-400
        backdrop: `rgba(0,0,0,0.4)`,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/history-member-batch-enrollment");
        }
      });

      // Reset states
      setFiles([]);
      setSuccessfulUploads([]);
    } catch (error) {
      setGlobalError(error.response?.data?.message || "Processing failed.");
      // Enhanced: Detailed Swal with retry
      MySwal.fire({
        icon: "error",
        title: "Processing Error",
        html: error.response?.data?.message || "Unknown error.",
        footer: `<div>Queued files: ${successfulUploads.length}</div>`,
        confirmButtonText: "Retry All",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          handleStartProcessing(); // Retry
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };
  const handleRemoveFile = (fileObj) => {
    setFiles((prev) => prev.filter((f) => f.file !== fileObj.file));
    if (fileObj.uploadId)
      setSuccessfulUploads((prev) =>
        prev.filter((id) => id !== fileObj.uploadId)
      );
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6">
      <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-xl p-4 sm:p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              {/* <FontAwesomeIcon icon={faUserPlus} className="mr-2 text-indigo-600" /> */}
              📤 Batch Member Enrollment: Uploading
            </h1>
          </div>
          {/* Top Actions */}
          <div className="mb-4 flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={handleDownloadCsvTemplate}
              className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
              data-tooltip-id="download-tooltip"
              data-tooltip-content="Download CSV template"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download CSV Template
            </button>
            <button
              type="button"
              onClick={handleDownloadExcelTemplate}
              className="inline-flex items-center px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
              data-tooltip-id="download-tooltip"
              data-tooltip-content="Download Excel template"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download Excel Template
            </button>
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
              data-tooltip-id="info-tooltip"
              data-tooltip-content="Toggle file structure and requirements"
            >
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              {showInfo ? "Hide Info" : "Show Info"}
            </button>
            <button
              type="button"
              onClick={() => setShowSpecsModal(true)}
              className="relative inline-flex items-center px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span className="absolute -right-2 -top-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow">
                New
              </span>
              <FontAwesomeIcon icon={faBookOpen} className="mr-2" />
              View File Specs
            </button>
            <button
              onClick={() => navigate("/history-member-batch-enrollment")}
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg"
              data-tooltip-id="history-tooltip"
              data-tooltip-content="View history"
            >
              <FontAwesomeIcon icon={faHistory} className="mr-2" />
              View Uploaded File History
            </button>
          </div>
          {/* Side-by-Side Layout */}
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            {/* Left: File Structure Info */}
            <div className={`lg:w-1/2 ${showInfo ? "block" : "hidden"}`}>
              <div className="p-3 bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <FontAwesomeIcon icon={faTableList} className="mr-2 text-blue-600" />
                  CSV/Excel File Structure
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Your file must have exactly 26 columns in this order. Use the
                  template download buttons for exact formatting.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 bg-white rounded overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Column
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Header Name
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Required?
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {headersInfo.map((item, index) => (
                        <tr
                          key={index}
                          className={item.required ? "bg-green-50/50" : "bg-white"}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.header}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                item.required
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {item.required ? (
                                <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-xs" />
                              ) : null}
                              {item.required ? "Required" : "Optional"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-500 max-w-xs">
                            {item.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Notes: Dates in strict MM/DD/YYYY format. Maximum data rows per file: {uploadLimits ? uploadLimits.max_rows.toLocaleString() : "Loading..."}.
                  Duplicates (name + DOB) not allowed within file or corporate.
                </p>
              </div>
            </div>



            {/* Right: Upload Area */}
            <div className="lg:w-1/2 space-y-4">


              {/* ⚠️ Warning Banner */}
              <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md shadow-sm">
                <div className="flex items-start">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-yellow-500 mr-3 mt-1"
                  />
                  <div>
                    <p className="text-sm text-yellow-800 font-semibold">
                      Important Notice:
                    </p>
                    <p className="text-sm text-yellow-700">
                      Once each user is successfully inserted, they will automatically receive a
                      <span className="font-semibold"> Welcome Notification</span> via their
                      <span className="font-semibold text-yellow-900"> Mobile Number</span> (if available),
                      otherwise through their <span className="font-semibold text-yellow-900"> Email Address</span>.
                      Mobile Number is prioritized when both are present.
                    </p>
                  </div>
                </div>


                {/* View Sample Buttons */}
                <div className="flex flex-wrap justify-end gap-3 mt-4">
                  {/* Email Button */}
                  <button
                    onClick={() => setShowEmailSample(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-md shadow hover:bg-green-700 transition-all duration-200"
                  >
                    <FontAwesomeIcon icon={faEnvelopeOpenText} />
                    View Sample Email Sent
                  </button>

                  {/* SMS Button */}
                  <button
                    onClick={() => setShowSmsSample(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md shadow hover:bg-blue-700 transition-all duration-200"
                  >
                    <FontAwesomeIcon icon={faMobileAlt} />
                    View Sample SMS Sent
                  </button>
                </div>


              </div>


              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                  isDragging
                    ? "border-indigo-500 bg-indigo-50/50 shadow-lg"
                    : "border-gray-300 hover:border-gray-400"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  disabled={isValidatingFiles}
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => handleFileChange(e.target.files)}
                  ref={fileInputRef}
                  className="hidden"
                />
                <FontAwesomeIcon
                  icon={faUpload}
                  className="text-3xl text-gray-400 mb-3"
                />
                <p className="text-base text-gray-600 mb-2">
                  Drag & drop CSV/Excel files here
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  {uploadLimits
                    ? `or click to browse (max ${maxUploadLimit} files, ${uploadLimits.max_file_size_mb} MB and ${uploadLimits.max_rows.toLocaleString()} data rows each)`
                    : "Loading upload limits..."}
                </p>
                {limitsError && <p className="text-xs text-red-600 mb-3">{limitsError}</p>}
                <p className="text-xs text-gray-500 mb-3">For large enrollments, use CSV. Excel files may require more processing memory.</p>
                <button
                  type="button"
                  disabled={isValidatingFiles}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-all duration-200 shadow-sm"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                  {isValidatingFiles ? "Validating files..." : "Browse Files"}
                </button>
              </div>
              {/* File List */}
              {files.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-base font-semibold text-gray-800 flex items-center">
                    <FontAwesomeIcon icon={faFile} className="mr-2 text-indigo-600" />
                    Selected Files ({files.length})
                  </h2>
                  <ul className="space-y-2">
                    {files.map((fileObj, index) => (
                      <li
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg shadow-sm border ${
                          fileObj.error
                            ? "bg-red-50 border-red-200"
                            : "bg-gray-100 border-gray-200 hover:bg-gray-200"
                        } transition-all duration-200`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate flex items-center">
                            <FontAwesomeIcon icon={faFile} className="mr-2 text-gray-500 text-xs" />
                            {fileObj.file.name}
                          </p>
                          {fileObj.isUploading && (
                            <div className="pt-1">
                              <div className="bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                                  style={{ width: `${fileObj.progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">
                                {fileObj.progress}%
                              </span>
                            </div>
                          )}
                          {fileObj.error && (
                            <div className="flex items-center text-xs text-red-600 mt-1">
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="mr-1"
                              />
                              <span
                                data-tooltip-id="error-tooltip"
                                data-tooltip-content={fileObj.error}
                              >
                                {fileObj.error.substring(0, 40)}...
                              </span>
                            </div>
                          )}
                          {fileObj.uploadId && (
                            <p className="text-xs text-green-600 flex items-center">
                              <FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-xs" />
                              ID: {fileObj.uploadId.substring(0, 8)}...
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveFile(fileObj)}
                          disabled={fileObj.isUploading}
                          className="text-red-500 hover:text-red-700 ml-3 disabled:opacity-50 transition-colors duration-200 p-1 rounded"
                          data-tooltip-id="remove-tooltip"
                          data-tooltip-content="Remove"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-sm" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Global Error Display */}
              {globalError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="text-red-600 mr-2"
                    />
                    <span className="text-red-800 text-sm flex-1">{globalError}</span>
                    <button
                      onClick={() => setGlobalError(null)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium ml-2"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setFiles([]);
                    setSuccessfulUploads([]);
                    setGlobalError(null);
                    toast.success("All files cleared.", { duration: 3000 });
                  }}
                  className="inline-flex items-center px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={files.length === 0}
                  data-tooltip-id="clear-tooltip"
                  data-tooltip-content="Clear all files"
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  Clear All
                </button>
                <button
                  type="button"
                  onClick={handleStartProcessing}
                  className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={successfulUploads.length === 0 || isProcessing}
                  data-tooltip-id="process-tooltip"
                  data-tooltip-content="Start processing uploaded files"
                >
                  {isProcessing ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-2"
                      />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlay} className="mr-2" />
                      Start Processing
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Loading Modal */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 flex flex-col items-center space-y-4 max-w-sm mx-auto shadow-2xl">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-3xl text-indigo-600 animate-spin"
              />
              <p className="text-lg font-medium text-gray-800 text-center">
                Processing Files...
              </p>
              <p className="text-sm text-gray-500 text-center">
                This may take a moment. Please wait.
              </p>
            </div>
          </div>
        )}
        {/* Tooltips */}
        <Tooltip id="remove-tooltip" place="top" />
        <Tooltip id="clear-tooltip" place="top" />
        <Tooltip id="process-tooltip" place="top" />
        <Tooltip id="download-tooltip" place="top" />
        <Tooltip id="history-tooltip" place="top" />
        <Tooltip id="error-tooltip" place="top" />
        <Tooltip id="info-tooltip" place="top" />
        <BatchFileSpecsModal
          endpoint={`${BASE_URL}/members/batch-enrollment/specs`}
          isOpen={showSpecsModal}
          onClose={() => setShowSpecsModal(false)}
          title="Batch Member Enrollment File Specs"
        />
      </div>



{/* Email Preview Modal */}
{showEmailSample && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faEnvelopeOpenText} className="mr-2 text-green-600" />
          Sample Email Sent (Welcome to AMAPHIL)
        </h2>
        <button
          onClick={() => setShowEmailSample(false)}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xl" />
        </button>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto bg-gray-100">
        <div className="p-4 sm:p-6 flex justify-center">
          <div
            className="w-full max-w-[600px] bg-white shadow rounded-lg overflow-hidden border border-gray-200"
            dangerouslySetInnerHTML={{ __html: `
              <!DOCTYPE html>
              <html>
              <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Welcome to AMAPHIL!</title>
              </head>
              <body style="margin:0;padding:0;font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;background-color:#f7f9fc;">
                  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f7f9fc;">
                      <tr>
                          <td align="center">
                              <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;margin:20px auto;border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,0.05);overflow:hidden;">
                                  <tr>
                                      <td style="background:linear-gradient(135deg,#97b50061,#83af34);padding:20px;text-align:center;">
                                          <table width="100%" cellpadding="0" cellspacing="0">
                                              <tr>
                                                  <td align="center" style="padding-bottom:10px;">
                                                      <img src="/img/logo-v2.png" alt="AMAPHIL Logo" style="max-width:160px;height:auto;">
                                                  </td>
                                              </tr>
                                          </table>
                                          <h1 style="color:white;margin:0;font-size:24px;font-weight:600;">Welcome to AMAPHIL!</h1>
                                          <p style="color:rgba(255,255,255,0.9);margin:8px 0 0;font-size:15px;">Dear John Doe</p>
                                      </td>
                                  </tr>
                                  <tr>
                                      <td style="padding:30px;">
                                          <p style="color:#4a5568;font-size:15px;line-height:1.5;margin:0 0 20px;">
                                              Welcome to AMAPHIL! Kindly visit Google Play or App Store and search AMAPHIL to download our mobile app.
                                          </p>
                                          <p style="color:#4a5568;font-size:15px;line-height:1.5;margin:0 0 20px;">
                                              <strong>Username:</strong> johndoe@email.com
                                          </p>
                                          <p style="color:#4a5568;font-size:15px;line-height:1.5;margin:0 0 20px;">
                                              <strong>Password:</strong> For your password, use your birthday in the format MMDDYY
                                          </p>
                                          <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#fff8e1;border-left:4px solid #ffc107;border-radius:4px;margin:0 0 20px;">
                                              <tr>
                                                  <td style="padding:12px 15px;">
                                                      <p style="color:#7d6608;font-size:14px;line-height:1.4;margin:0;font-weight:600;">📞 Need assistance?</p>
                                                      <p style="color:#7d6608;font-size:13px;line-height:1.4;margin:5px 0 0;">
                                                          Call (02)83557500 or 09399140866
                                                      </p>
                                                  </td>
                                              </tr>
                                          </table>
                                      </td>
                                  </tr>
                                  <tr>
                                      <td style="background-color:#f8f9fa;padding:20px;border-top:1px solid #e2e8f0;">
                                          <p style="color:#718096;font-size:13px;line-height:1.5;margin:0;">
                                              Thank you!<br><strong style="color:#2d3748;">AMAPHIL Team</strong>
                                          </p>
                                          <hr style="border:none;border-top:1px solid #e2e8f0;margin:12px 0;">
                                          <p style="color:#2d3748;font-size:13px;font-weight:600;margin:0 0 5px;">ADVANCED MEDICAL ACCESS PHILIPPINES, INC.</p>
                                          <p style="color:#4a5568;font-size:11px;line-height:1.4;margin:0;">820 JP Rizal Street, Barangay Poblacion, Makati City</p>
                                          <p style="color:#4a5568;font-size:11px;line-height:1.4;margin:0;">T (02) 8241-7679 | (02) 8355-7500</p>
                                          <p style="color:#4a5568;font-size:11px;line-height:1.4;margin:0;">E info@amaphil.com.ph</p>
                                          <p style="color:#4a5568;font-size:11px;line-height:1.4;margin:0;">W www.amaphil.com.ph</p>
                                      </td>
                                  </tr>
                                  <tr>
                                      <td style="background-color:#f1f5f9;padding:15px 20px;border-top:1px solid #e2e8f0;">
                                          <p style="color:#718096;font-size:10px;line-height:1.4;margin:0;font-style:italic;">
                                              CONFIDENTIAL: This message and any attached documents are intended solely for the
                                              named recipient(s) and may contain confidential and privileged information. Any unauthorized
                                              disclosure, use, or copying is strictly prohibited. If you have received this in error, please
                                              immediately notify the sender and permanently delete the message.
                                          </p>
                                          <p style="color:#4a5568;font-size:10px;line-height:1.4;margin:5px 0 0;">
                                              For inquiries, please contact: <span style="color:#3a7bd5;">info@amaphil.com.ph</span>
                                          </p>
                                      </td>
                                  </tr>
                              </table>
                          </td>
                      </tr>
                  </table>
              </body>
              </html>
            ` }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end px-5 py-3 border-t bg-gray-50">
        <button
          onClick={() => setShowEmailSample(false)}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-all text-sm font-medium"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}



{/* Sample SMS Modal */}
{showSmsSample && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faMobileAlt} className="mr-2 text-blue-600" />
          Sample SMS Sent
        </h2>
        <button
          onClick={() => setShowSmsSample(false)}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xl" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-gray-100">
        <div className="p-6">
          <div className="bg-white border border-gray-200 rounded-lg shadow-inner p-4 text-gray-800 font-mono text-sm whitespace-pre-line leading-relaxed">
            {`Dear John Doe
Welcome to AMAPHIL! Kindly visit Google Play or App Store and search AMAPHIL to download our mobile app.
USERNAME: johndoe@email.com
For your PASSWORD, use your birthday with this format MMDDYY
Need assistance? Call (02)83557500; 09399140866
Thank you!`}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end px-5 py-3 border-t bg-gray-50">
        <button
          onClick={() => setShowSmsSample(false)}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-all text-sm font-medium"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}




    </div>
  );
};
export default UploadMemberEnrollment;
